import { useState } from "react";
import { z } from "zod";
import { CheckCircle2, ArrowRight, Loader2, Bell } from "lucide-react";
import { PageHero } from "@/components/site-layout";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const schema = z.object({
  fullName: z.string().trim().min(2, "Please enter your full name").max(100),
  email: z.string().trim().email("Enter a valid email address").max(255),
  phone: z.string().trim().min(7, "Enter a valid phone number").max(20),
  location: z.string().trim().max(100).optional().or(z.literal("")),
  additionalInformation: z.string().trim().max(500).optional().or(z.literal("")),
});

type FormState = z.infer<typeof schema>;

const INITIAL: FormState = {
  fullName: "",
  email: "",
  phone: "",
  location: "",
  additionalInformation: "",
};

export default function WaitlistPage() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    const result = schema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof FormState, string>> = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof FormState;
        fieldErrors[field] = issue.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setStatus("loading");

    try {
      const response = await fetch("/api/?action=waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error(data.message || "You are already on our waitlist");
        }
        throw new Error(data.message || "Failed to join waitlist");
      }

      setStatus("done");
      
      Swal.fire({
        icon: 'success',
        title: "You're on the list!",
        text: "Thank you for your interest in RevLinks Training Institute. We'll contact you when applications officially open.",
        confirmButtonColor: '#d64545',
        confirmButtonText: 'OK'
      });

      setForm(INITIAL);
    } catch (error: any) {
      setStatus("idle");
      const errorMessage = error.message || "Failed to join waitlist. Please try again.";
      
      // Check if it's a duplicate email error
      if (errorMessage.includes("already on our waitlist") || errorMessage.includes("already exists")) {
        Swal.fire({
          icon: 'warning',
          title: 'Already on the Waitlist',
          text: 'This email address is already on our waitlist. We\'ll contact you when applications officially open.',
          confirmButtonColor: '#d64545',
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Submission Failed',
          text: errorMessage,
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  if (status === "done") {
    return (
      <div className="min-h-screen bg-background">
        <section className="container-x flex min-h-[70vh] items-center justify-center py-20">
          <div className="w-full max-w-lg rounded-3xl border border-border bg-card p-10 text-center shadow-[var(--shadow-elegant)] animate-fade-up">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-500/10 text-green-500">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="mt-6 text-3xl font-bold">You're on the list!</h1>
            <p className="mt-3 text-muted-foreground">
              Thank you for your interest in RevLinks Training Institute. We'll contact you when applications officially open.
            </p>
            <button
              onClick={() => {
                setStatus("idle");
                setForm(INITIAL);
              }}
              className="btn-primary mt-8 w-full"
            >
              Submit another entry
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHero
        eyebrow="Waitlist"
        title={<>Join the <span className="gradient-text">Waitlist</span>.</>}
        intro="Applications for our upcoming training programme will be opening soon. Join the waitlist and we'll notify you when applications officially open."
      />

      <section className="container-x pb-24 pt-12">
        <div className="mx-auto max-w-md mb-8 rounded-2xl border border-primary/20 bg-primary/5 p-4 text-center">
          <p className="text-sm font-medium text-primary">
            <strong>Important:</strong> This is a physical training programme held in Lagos, Nigeria.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            <strong>Address:</strong> 130b Oba Ladejobi, Ikeja GRA, Lagos, Nigeria
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            <strong>Contact:</strong> +234 07053944594
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="mx-auto max-w-md rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-elegant)] animate-fade-up md:p-10"
        >
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[image:var(--gradient-primary)] text-primary-foreground">
            <Bell className="h-6 w-6" />
          </div>
          
          <label className="mt-6 block">
            <span className="mb-1.5 block text-sm font-medium">Full Name *</span>
            <input
              type="text"
              value={form.fullName}
              onChange={(e) => update("fullName", e.target.value)}
              placeholder="Your full name"
              className={`w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.fullName ? 'border-red-500' : ''}`}
            />
            {errors.fullName && <p className="mt-1 text-xs text-destructive">{errors.fullName}</p>}
          </label>
          
          <label className="mt-4 block">
            <span className="mb-1.5 block text-sm font-medium">Email Address *</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@example.com"
              className={`w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.email ? 'border-red-500' : ''}`}
            />
            {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
          </label>
          
          <label className="mt-4 block">
            <span className="mb-1.5 block text-sm font-medium">Phone Number *</span>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+234 XXX XXX XXXX"
              className={`w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.phone ? 'border-red-500' : ''}`}
            />
            {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
          </label>
          
          <label className="mt-4 block">
            <span className="mb-1.5 block text-sm font-medium">Location</span>
            <input
              type="text"
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder="City, State"
              className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </label>
          
          <label className="mt-4 block">
            <span className="mb-1.5 block text-sm font-medium">Additional Information</span>
            <textarea
              value={form.additionalInformation}
              onChange={(e) => update("additionalInformation", e.target.value)}
              placeholder="Any specific questions or requirements?"
              rows={3}
              className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none"
            />
          </label>
          
          <button type="submit" disabled={status === "loading"} className="btn-primary mt-6 w-full">
            {status === "loading" ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</>
            ) : (
              <>Join the Waitlist <ArrowRight className="h-4 w-4" /></>
            )}
          </button>
          
          <p className="mt-6 text-xs text-muted-foreground">
            By joining the waitlist, you agree to receive notifications about our training programmes and application opening dates.
          </p>
        </form>
      </section>
    </div>
  );
}